/**
 * CosteHilo — GEN-MO-024: el mismo cuadro de coste que LlamadasTurno, pero de TODA la conversación
 * (una fila por paso, modelo y regla), con «Coste del hilo: X €» como botón desplegable. Solo se monta
 * para el Owner (admin); la puerta real es GET /v1/admin/chats/{chat_id}/coste (403 al resto). El kernel
 * suma y valora; aquí solo se pintan. `version` cambia al cerrarse un turno y vuelve a pedir el cuadro.
 */

import { useEffect, useState } from 'react'
import { getCosteHilo } from '../api/client.js'
import { etiquetaRegla, eur, tokens } from './LlamadasTurno.jsx'

const SESIONES = [['resume', 'resume'], ['nueva', 'nueva'], ['fallback', 'fallback']]

export default function CosteHilo({ chatId, version = 0 }) {
  const [datos, setDatos] = useState(null)
  const [abierto, setAbierto] = useState(false)

  useEffect(() => {
    let vivo = true
    getCosteHilo(chatId)
      .then(d => vivo && setDatos(d))
      // Sin cuadro no se estorba al hilo: el chat aún sin guardar (404) o un fallo puntual no pintan nada.
      .catch(() => vivo && setDatos(null))
    return () => { vivo = false }
  }, [chatId, version])

  if (!datos || !datos.filas) return null

  const cache = datos.filas.reduce(
    (a, f) => ({ lectura: a.lectura + f.cache_read_input_tokens, escritura: a.escritura + f.cache_creation_input_tokens }),
    { lectura: 0, escritura: 0 },
  )
  const sesiones = SESIONES.filter(([k]) => datos.sesion?.[k] > 0).map(([k, t]) => `${datos.sesion[k]} ${t}`)

  return (
    <div className="coste-hilo">
      <button
        type="button"
        className="coste-hilo__btn"
        aria-expanded={abierto}
        title="Ver el desglose de coste de toda la conversación"
        onClick={() => setAbierto(v => !v)}
      >
        Coste del hilo: {eur(datos.total_eur)}
        {datos.estimado && <> <span className="coste-marca">estimado</span></>}
      </button>
      {abierto && (
        <div className="coste-hilo__cuadro">
          <table className="mec-tabla llamadas-turno" aria-label="Coste del hilo">
            <thead>
              <tr><th>Paso</th><th>Regla</th><th>Modelo</th><th className="coste-eur">Tokens</th><th className="coste-eur">Coste</th></tr>
            </thead>
            <tbody>
              {datos.filas.map((f, i) => {
                const regla = f.fallido ? { texto: '—', clave: null } : etiquetaRegla(f.regla, f.clasificacion_origen)
                return (
                  <tr key={`${f.paso}|${f.modelo}|${f.regla}|${i}`}>
                    <td>{f.paso}</td>
                    <td title={regla.clave ?? undefined}>{regla.texto}</td>
                    <td>{f.modelo || '—'}</td>
                    <td className="coste-eur">{tokens(f)}</td>
                    <td className="coste-eur">{eur(f.eur)} {f.estimado && <span className="coste-marca">estimado</span>}</td>
                  </tr>
                )
              })}
              <tr className="coste-total">
                <td colSpan={4}>Total del hilo</td>
                <td className="coste-eur">{eur(datos.total_eur)}</td>
              </tr>
            </tbody>
          </table>
          {(cache.lectura > 0 || cache.escritura > 0 || sesiones.length > 0) && (
            <p className="coste-nota">
              {(cache.lectura > 0 || cache.escritura > 0) &&
                `Caché: ${cache.lectura.toLocaleString('es-ES')} tokens leídos · ${cache.escritura.toLocaleString('es-ES')} escritos.`}
              {sesiones.length > 0 && ` Turnos: ${sesiones.join(' · ')}.`}
            </p>
          )}
          {datos.truncado && <p className="coste-nota">Hilo muy largo: el cuadro cubre solo los primeros {datos.turnos} turnos.</p>}
          {datos.fallidos_sin_separar && (
            <p className="coste-nota">Este hilo tiene turnos anteriores a GEN-MO-017: sus llamadas fallidas no se pueden separar.</p>
          )}
        </div>
      )}
    </div>
  )
}
